import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Video, Upload, ArrowLeft, Lock } from "lucide-react";
import api, { apiErr, FILE_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VideoRecorder from "@/components/VideoRecorder";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

function VideoVignette({ c }) {
  const src = c.video_url ? `${FILE_BASE}${c.video_url}` : null;
  return (
    <Link to={`/candidat/${c.slug}`} data-testid={`contest-card-${c.slug}`} className="card-cocarde overflow-hidden group">
      <div className="relative aspect-[3/4] bg-cc-navy overflow-hidden">
        {src ? (
          <video src={src} poster={c.poster_url ? `${FILE_BASE}${c.poster_url}` : undefined} autoPlay muted loop playsInline preload="metadata" data-testid={`contest-video-${c.slug}`}
            onTimeUpdate={(e) => { if (e.target.currentTime > 5) e.target.currentTime = 0; }}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full grid place-items-center bg-gradient-to-br from-cc-navy to-[#12336b]">
            <div className="rosette w-16 h-16 opacity-80 animate-spin-slow" />
          </div>
        )}
        <span className={`absolute top-2 left-2 font-display text-lg rounded-full px-2.5 py-0.5 ${c.rank === 1 ? "bg-cc-gold text-cc-navy" : "bg-black/60 text-white"}`}>#{c.rank}</span>
        <span className="absolute top-2 right-2 bg-cc-red text-white text-xs font-bold rounded-full px-2 py-0.5">{c.votes} votes</span>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3 pt-8">
          <p className="text-white font-display text-lg leading-tight truncate">{c.display_name}</p>
          <p className="text-white/70 text-xs truncate">{c.program}</p>
        </div>
      </div>
    </Link>
  );
}

export default function EditionPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [edition, setEdition] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/editions/${slug}`).then((r) => setEdition(r.data)).catch(() => setEdition({ name: slug, candidates: [], boxes: [] }));
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [slug]);

  const status = edition?.status || "open";
  const startCandidacy = () => {
    if (status !== "open") { toast.error(status === "closed" ? "Cette édition est clôturée" : "Cette édition n'est pas encore ouverte"); }
    else if (!user) { toast.error("Connectez-vous en tant que client pour vous présenter"); nav("/login"); }
    else if (user.role !== "client") { toast.error("Seuls les clients peuvent se présenter"); }
    else setOpen(true);
  };

  const candidates = edition?.candidates || [];

  return (
    <div>
      <section className="bg-cc-navy text-white py-14 relative overflow-hidden grain">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <Link to="/concours" data-testid="edition-back" className="flex items-center justify-center gap-1 text-white/60 hover:text-cc-gold text-sm mb-4"><ArrowLeft size={15} /> Toutes les éditions</Link>
          <span className="eyebrow text-cc-gold block">Édition · Jeu concours</span>
          <h1 data-testid="edition-title" className="font-display text-5xl sm:text-6xl mt-3">{(edition?.name || slug).toUpperCase()}</h1>
          {(edition?.start_date || edition?.end_date) && (
            <p data-testid="edition-dates" className="text-white/60 text-sm mt-2 font-mono">
              {edition.start_date ? `Du ${fmtDate(edition.start_date)} ` : ""}{edition.end_date ? `au ${fmtDate(edition.end_date)}` : ""}
            </p>
          )}
          <p className="text-white/70 mt-4 max-w-xl mx-auto">{edition?.description || "Filmez-vous dans la boîte, décrivez votre programme et grimpez au classement de l'édition. Le peuple vote !"}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            {status === "open" ? (
              <button data-testid="contest-candidate-btn" onClick={startCandidacy} className="btn-cocarde inline-flex items-center gap-2"><Video size={18} /> Je me présente</button>
            ) : (
              <span data-testid="edition-closed-banner" className="inline-flex items-center gap-2 bg-white/15 rounded-full px-5 py-2.5 font-bold">
                <Lock size={16} className="text-cc-gold" /> {status === "closed" ? "Édition clôturée · classement final" : `Ouverture le ${fmtDate(edition.start_date)}`}
              </span>
            )}
          </div>
        </div>
      </section>

      <section id="candidats" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-3 mb-6">
          {status === "closed" ? <h2 className="font-display text-3xl sm:text-4xl cc-navy">CLASSEMENT FINAL</h2> : <><span className="rec-dot" /><h2 className="font-display text-3xl sm:text-4xl cc-navy">LES CANDIDATS · SONDAGE EN DIRECT</h2></>}
        </div>
        {candidates.length === 0 ? (
          <div data-testid="edition-empty" className="card-cocarde p-10 text-center text-slate-500">
            Aucun candidat pour cette édition{status === "open" ? " pour l'instant. " : "."}{status === "open" && <button onClick={startCandidacy} className="text-cc-red font-bold">Soyez le premier !</button>}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {candidates.map((c) => <VideoVignette key={c.id} c={c} />)}
          </div>
        )}
        <p className="text-center text-slate-400 text-sm mt-8 font-mono">Les vignettes rejouent les 5 premières secondes en boucle. Partagez votre lien avec #cocardepizza.</p>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[92vh] overflow-y-auto">
          <DialogTitle className="font-display text-2xl cc-navy">Déposer ma candidature</DialogTitle>
          <CandidacyForm edition={slug} onDone={() => { setOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CandidacyForm({ edition, onDone }) {
  const [blob, setBlob] = useState(null);
  const [name, setName] = useState("");
  const [program, setProgram] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!blob) { toast.error("Enregistrez d'abord votre vidéo (30 sec)"); return; }
    if (!name || !program) { toast.error("Renseignez votre nom de campagne et votre programme"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "candidature.webm");
      const { data: up } = await api.post("/upload/video", fd);
      await api.post("/candidates", { display_name: name, program, video_file_id: up.file_id, edition });
      toast.success("Candidature envoyée ! En attente de validation par la startup.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 mt-2">
      <VideoRecorder onRecorded={setBlob} />
      {blob && <p className="text-sm text-green-600 font-bold flex items-center gap-1"><Upload size={14} /> Vidéo prête à être envoyée</p>}
      <input data-testid="candidacy-name" placeholder="Votre nom de campagne" value={name} onChange={(e) => setName(e.target.value)}
        className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:border-cc-red outline-none" />
      <textarea data-testid="candidacy-program" placeholder="Votre programme en quelques mots…" value={program} onChange={(e) => setProgram(e.target.value)} rows={3}
        className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:border-cc-red outline-none" />
      <button data-testid="candidacy-submit" onClick={submit} disabled={busy} className="btn-cocarde w-full disabled:opacity-60">
        {busy ? "Envoi…" : "Envoyer ma candidature"}
      </button>
    </div>
  );
}
